'use client';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Trash2, Edit, Search, UserPlus, User as UserIcon, Shield, Key, Mail, CheckCircle, XCircle } from 'lucide-react';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  User,
  CreateUserData,
  UserPassword,
  UpdatePassword,
  updatePass,
} from './user';
import { Header, useModal, Onboarding, toast } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { Types as coreTypes } from '@ohif/core';
import { useAppConfig } from '@state';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

function AdminPanel({ servicesManager }) {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDialogOpenPass, setIsDialogOpenPass] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingUserPassword, setEditingUserPassword] = useState<UserPassword | null>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    name: '',
    email: '',
    contraseña: '',
    role: 'user',
    vistas: ['profile', 'dashboard'],
    status: 'active',
  });
  const [formDataPass, setFormDataPass] = useState<UpdatePassword>({
    password: '',
    newpassword: '',
    newpasswordConfirmate: '',
  });

  const { t } = useTranslation();
  const { show } = useModal();
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();

  const { menuOptions } = getHeaderConfig({
    servicesManager,
    t,
    show,
    navigate,
    appConfig,
  });

  const filteredUsers = users.filter(
    user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Estado para indicar que verificamos la sesión (token) al iniciarse el componente
  const [isSessionChecked, setIsSessionChecked] = useState(false);

  useEffect(() => {
    // Verificar si hay token en localStorage
    const localStr = localStorage.getItem('usuario');
    const session = localStr ? JSON.parse(localStr) : null;

    if (!session?.token) {
      // No hay token: redirigir al login
      toast.warning('Hubo un error no tienes permisos para esta pagina');
      navigate('/');
      return;
    }

    // Ya tenemos token, podemos proceder a cargar usuarios
    setIsSessionChecked(true);
  }, [navigate]);

  useEffect(() => {
    if (!isSessionChecked) {
      return; // Esperar a que se verifique la sesión antes de hacer la petición
    }

    // Ya con el token asegurado, hacer la petición
    fetchUsers()
      .then(apiUsers => {
        const mapped: any = apiUsers.map(user => ({
          id: user._id,
          name: user.nombre,
          email: user.correo,
          role: user.role,
          vistas: user.vistas ?? [],
          status: user.status,
          createdAt: user.fechaCreacion?.split('T')[0] || '',
        }));
        setUsers(mapped);
      })
      .catch(error => {
        toast.error('Error al obtener usuarios: ' + (error as any).message);
        // Si es 401, podrías redirigir al login también
        // navigate('/login');
      });
  }, [isSessionChecked]);

  const handleCreateUser = async () => {
    try {
      const crear: any = await createUser({
        nombre: formData.name,
        correo: formData.email,
        contraseña: formData.contraseña,
        vistas: formData.vistas,
        role: formData.role,
        status: formData.status,
      });
      // Recargar lista de usuarios después de crear
      const apiUsers = await fetchUsers();
      const mapped: any = apiUsers.map(user => ({
        id: user._id,
        name: user.nombre,
        email: user.correo,
        vistas: user.vistas ?? [],
        role: user.role,
        status: user.status,
        createdAt: user.fechaCreacion?.split('T')[0] || '',
      }));
      setUsers(mapped);

      toast.success(crear?.message + ' ' + crear?.user.nombre);
      setIsDialogOpen(false);
    } catch (err: any) {
      toast.error('Error al crear usuario: ' + err.message || err);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) {
      return;
    }
    try {
      const editar: any = await updateUser(editingUser.id, {
        nombre: formData.name,
        correo: formData.email,
        role: formData.role,
        vistas: formData.vistas,
        status: formData.status,
      });
      toast.success(editar?.message + ' ' + editar?.user.nombre);

      setUsers(users =>
        users.map(user =>
          user.id === editingUser.id
            ? { ...user, name: formData.name, email: formData.email, role: formData.role, status: formData.status }
            : user
        )
      );
      setIsDialogOpen(false);
    } catch (err: any) {
      toast.error('Error al actualizar usuario: ' + err.message || err);
    }
  };

  const handleUserPassword = async () => {
    if (!editingUserPassword) {
      return;
    }
    try {
      const editar: any = await updatePass(editingUserPassword.id, formDataPass);
      toast.success(editar?.message);
      setIsDialogOpenPass(false);
    } catch (err: any) {
      toast.error('Error al actualizar usuario: ' + err.message || err);
    }
  };

  const changesView = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedRole = e.target.value as typeof formData.role;
    let updatedVistas: string[] = [];

    if (selectedRole === 'traumatologia' || selectedRole === 'sala de dictados rayos x') {
      updatedVistas = ['RAYOS X'];
    } else if (selectedRole === 'pediatria') {
      updatedVistas = ['VISTA COMMON', 'DOC'];
    } else if (selectedRole === 'medicoExterno') {
      updatedVistas = ['DESCARGA DE SERIES', 'INFORMES', 'DOC', 'VISTA COMMON'];
    } else if (selectedRole === 'radiologiaInterno') {
      updatedVistas = ['DESCARGA DE SERIES', 'INFORMES', 'DOC', 'VISTA COMMON', 'MPR ADVANCED'];
    } else if (selectedRole === 'radiologiaTecnico') {
      updatedVistas = ['DESCARGA DE SERIES', 'DOC', 'VISTA COMMON'];
    } else if (selectedRole === 'enfermeria') {
      updatedVistas = ['VISTA COMMON', 'DOC'];
    } else if (selectedRole === 'administrativo') {
      updatedVistas = ['DOC'];
    } else if (selectedRole === 'emergencias') {
      updatedVistas = ['VISTA COMMON', 'DOC'];
    } else if (selectedRole === 'sala de dictados tomografia') {
      updatedVistas = ['TOMOGRAFIA'];
    } else if (selectedRole === 'medico general' || selectedRole === 'admin') {
      updatedVistas = [
        'MAMOGRAFIA',
        'TOMOGRAFIA',
        'RESONANCIA',
        'ULTRASONIDO',
        'RAYOS X',
        'DOC',
        'INFORMES',
        'LINK DE ENVIO',
        'DESCARGA DE SERIES',
        'SEND DICOM',
        'MPR ADVANCED',
        'VISTA COMMON',
        'COMENTAR',
        'COMPARTIR',
      ];
    }

    setFormData({
      ...formData,
      role: selectedRole,
      vistas: updatedVistas,
    });
  };

  const handleDeleteUser = async (id: string) => {
    try {
      await deleteUser(id);
      toast.success('Usuario eliminado correctamente');
      setUsers(users => users.filter(user => user.id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar el usuario: ' + err.message || err);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      contraseña: '',
      role: user.role,
      vistas: user.vistas || ['profile', 'dashboard'],
      status: user.status,
    });
    setIsDialogOpen(true);
  };

  const handleEditUserPassword = (user: UserPassword) => {
    setEditingUserPassword(user);
    setFormDataPass({
      password: user.password,
      newpassword: user.newpassword,
      newpasswordConfirmate: user.newpasswordConfirmate,
    });
    setIsDialogOpenPass(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contraseña: '',
      email: '',
      role: 'user',
      vistas: ['profile', 'dashboard'],
      status: 'active',
    });
    setEditingUser(null);
    setIsDialogOpen(false);
    setIsDialogOpenPass(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingUser ? handleUpdateUser() : handleCreateUser();
  };
  const handleSubmitPass = (e: React.FormEvent) => {
    e.preventDefault();
    handleUserPassword();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Panel de Administración</h1>
            <p className="mt-1">Gestiona los usuarios del sistema</p>
          </div>
          <button
            onClick={() => {
              setEditingUser(null);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              fill="currentColor"
              className="bi bi-person-fill-add"
              viewBox="0 0 16 16"
            >
              <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.5-5v1h1a.5.5 0 0 1 0 1h-1v1a.5.5 0 0 1-1 0v-1h-1a.5.5 0 0 1 0-1h1v-1a.5.5 0 0 1 1 0m-2-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
              <path d="M2 13c0 1 1 1 1 1h5.256A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1 1.544-3.393Q8.844 9.002 8 9c-5 0-6 3-6 4" />
            </svg>
          </button>
        </div>

        {/* Buscador y lista */}
        <div className="bg-primary-dark mt-5 rounded-lg shadow-xl overflow-hidden border border-gray-700">
          <div className="bg-secondary-dark flex flex-col justify-between gap-4 p-5 md:flex-row border-b border-gray-700 items-center">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-primary-main" /> Usuarios Registrados
                <span className="text-sm font-normal bg-primary-main/20 text-primary-main px-2 py-0.5 rounded-full">{filteredUsers.length}</span>
              </h2>
              <p className="text-sm text-gray-400 mt-1">Gestiona los permisos y accesos de tu equipo</p>
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="focus:ring-primary-main w-full rounded-md border border-gray-600 bg-black py-2.5 pl-10 pr-3 text-sm text-white focus:outline-none focus:ring-2 transition-shadow"
                placeholder="Buscar por nombre o correo..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 overflow-y-auto p-5 max-h-[60vh] bg-black/20">
            {filteredUsers.length > 0 ? (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="bg-secondary-dark hover:border-primary-main/50 transition-colors gap-4 rounded-md border border-gray-700 p-4 shadow-sm max-sm:flex-col sm:flex sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-primary-main/10 text-primary-main h-10 w-10 rounded-full flex items-center justify-center font-bold text-lg border border-primary-main/30">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white">{user.name}</h3>
                      <p className="text-sm text-gray-400 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {user.email}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 mt-4 sm:mt-0">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium uppercase border ${
                        user.role === 'admin'
                          ? 'bg-blue-900/40 text-blue-400 border-blue-500'
                          : 'bg-gray-800 text-gray-300 border-gray-600'
                      }`}
                    >
                      <Shield className="w-3 h-3 inline mr-1 -mt-0.5" />
                      {user.role}
                    </span>
                    <span
                      className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border ${
                        user.status === 'active'
                          ? 'bg-green-900/40 text-green-400 border-green-500'
                          : 'bg-red-900/40 text-red-400 border-red-500'
                      }`}
                    >
                      {user.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {user.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 mt-4 sm:mt-0">
                    <p className="text-xs text-gray-500 hidden md:block">
                      {user.createdAt}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          handleEditUserPassword({
                            id: user.id,
                            password: '',
                            newpassword: '',
                            newpasswordConfirmate: '',
                          })
                        }
                        className="p-2 text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10 rounded transition-colors"
                        title="Cambiar Contraseña"
                      >
                        <Key className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 rounded transition-colors"
                        title="Editar Usuario"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded transition-colors"
                        title="Eliminar Usuario"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-12 flex flex-col items-center justify-center text-gray-400">
                <Search className="w-12 h-12 mb-4 opacity-20" />
                <p className="text-lg">No se encontraron usuarios</p>
                <p className="text-sm opacity-60">Intenta con otro término de búsqueda</p>
              </div>
            )}
          </div>
        </div>

        {isDialogOpenPass && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-primary-dark max-w-md rounded p-6 shadow-lg sm:w-full">
              <h3 className="mb-1 text-lg font-semibold">Actualizar Contraseña</h3>
              <form
                onSubmit={handleSubmitPass}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium">Contraseña</label>
                  <input
                    type="password"
                    name="password"
                    value={formDataPass.password}
                    onChange={e => setFormDataPass({ ...formDataPass, password: e.target.value })}
                    className={
                      'focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring'
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Nueva contraseña</label>
                  <input
                    type="password"
                    name="newpassword"
                    value={formDataPass.newpassword}
                    onChange={e =>
                      setFormDataPass({ ...formDataPass, newpassword: e.target.value })
                    }
                    className="focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Confirmar Contraseña</label>
                  <input
                    type="password"
                    name="newpasswordConfirmate"
                    value={formDataPass.newpasswordConfirmate}
                    onChange={e =>
                      setFormDataPass({ ...formDataPass, newpasswordConfirmate: e.target.value })
                    }
                    className={
                      'focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring'
                    }
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded bg-red-700 px-4 py-2 text-white hover:bg-red-500"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-900"
                  >
                    {'Actualizar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {isDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-primary-dark w-full max-w-4xl rounded-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="bg-secondary-dark px-6 py-4 border-b border-gray-700 flex justify-between items-center">
                <h3 className="text-xl font-bold flex items-center gap-2 text-white">
                  <UserPlus className="h-6 w-6 text-primary-main" />
                  {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                </h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-white">
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 p-6">
                <form id="user-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Columna 1: Registro */}
                  <div className="space-y-5">
                    <h4 className="text-md font-semibold text-gray-300 flex items-center gap-2 border-b border-gray-700 pb-2">
                      <UserIcon className="h-5 w-5" /> Datos de Registro
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-300">Nombre Completo</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        className="focus:ring-primary-main w-full rounded border border-gray-600 bg-black px-3 py-2 text-white focus:outline-none focus:ring-2 mt-1"
                        placeholder="Ej. Juan Pérez"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300">Correo Electrónico</label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={e => setFormData({ ...formData, email: e.target.value })}
                          className="focus:ring-primary-main w-full rounded border border-gray-600 bg-black py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2"
                          placeholder="usuario@hospital.com"
                          required
                        />
                      </div>
                    </div>

                    {/* Solo mostramos la contraseña si NO estamos editando */}
                    {!editingUser && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300">Contraseña</label>
                        <div className="relative mt-1">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="password"
                            value={formData.contraseña}
                            onChange={e => setFormData({ ...formData, contraseña: e.target.value })}
                            className="focus:ring-primary-main w-full rounded border border-gray-600 bg-black py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2"
                            placeholder="Asigna una contraseña segura"
                            required={!editingUser}
                          />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300">Rol Principal</label>
                        <select
                          value={formData.role}
                          onChange={changesView}
                          className="focus:ring-primary-main mt-1 w-full rounded border border-gray-600 bg-black px-3 py-2 text-white focus:outline-none focus:ring-2"
                        >
                          <option value="user">Usuario</option>
                          <option value="administrativo">Administrativo</option>
                          <option value="admin">Administrador</option>
                          <option value="enfermeria">Enfermería</option>
                          <option value="emergencias">Emergencias</option>
                          <option value="traumatologia">Traumatología</option>
                          <option value="pediatria">Pediatría</option>
                          <option value="radiologiaTecnico">Técnico Radiólogo</option>
                          <option value="radiologiaInterno">Médico Radiólogo Interno</option>
                          <option value="medicoExterno">Médico Externo</option>
                          <option value="sala de dictados rayos x">Sala de dictados Rayos X</option>
                          <option value="sala de dictados tomografia">Sala de dictados Tomografía</option>
                          <option value="medico general">Médico General</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Estado de la cuenta</label>
                        <div className="flex rounded-md shadow-sm mt-1">
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, status: 'active' })}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-l-md border ${
                              formData.status === 'active'
                                ? 'bg-green-900/40 text-green-400 border-green-500 z-10'
                                : 'bg-black text-gray-400 border-gray-600 hover:bg-gray-800'
                            }`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            Activo
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, status: 'inactive' })}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-r-md border -ml-px ${
                              formData.status === 'inactive'
                                ? 'bg-red-900/40 text-red-400 border-red-500 z-10'
                                : 'bg-black text-gray-400 border-gray-600 hover:bg-gray-800'
                            }`}
                          >
                            <XCircle className="w-4 h-4" />
                            Inactivo
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Columna 2: Permisos */}
                  <div className="space-y-5">
                    <h4 className="text-md font-semibold text-gray-300 flex items-center gap-2 border-b border-gray-700 pb-2">
                      <Shield className="h-5 w-5 text-green-400" /> Permisos y Accesos (Vistas)
                    </h4>
                    <p className="text-sm text-gray-400">Selecciona los módulos a los que el usuario tendrá acceso.</p>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      {[
                        'MAMOGRAFIA',
                        'TOMOGRAFIA',
                        'RESONANCIA',
                        'ULTRASONIDO',
                        'RAYOS X',
                        'DOC',
                        'INFORMES',
                        'LINK DE ENVIO',
                        'DESCARGA DE SERIES',
                        'SEND DICOM',
                        'MPR ADVANCED',
                        'VISTA COMMON',
                        'COMENTAR',
                        'COMPARTIR',
                      ].map(vista => {
                        const isChecked = formData.vistas.includes(vista);
                        return (
                          <label
                            key={vista}
                            className={`flex cursor-pointer items-center space-x-2 rounded border p-2.5 text-sm transition-colors ${
                              isChecked
                                ? 'border-primary-main bg-primary-dark/30 text-white'
                                : 'border-gray-700 bg-black text-gray-400 hover:border-gray-500 hover:text-gray-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              value={vista}
                              checked={isChecked}
                              onChange={e => {
                                const checked = e.target.checked;
                                setFormData(prev => ({
                                  ...prev,
                                  vistas: checked
                                    ? [...prev.vistas, vista]
                                    : prev.vistas.filter(v => v !== vista),
                                }));
                              }}
                              className="text-primary-main focus:ring-primary-main h-4 w-4 rounded border-gray-600 bg-black focus:ring-2"
                            />
                            <span className="truncate text-xs font-medium" title={vista}>
                              {vista}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </form>
              </div>

              <div className="bg-secondary-dark px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded border border-gray-600 px-5 py-2 text-white hover:bg-gray-700 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  form="user-form"
                  className="flex items-center gap-2 rounded bg-primary-secundary px-6 py-2 text-white hover:bg-primary-dark font-medium transition-colors shadow-lg shadow-blue-900/20"
                >
                  <CheckCircle className="h-4 w-4" />
                  {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function getHeaderConfig({ servicesManager, t, show, navigate, appConfig }) {
  const { customizationService } = servicesManager.services;

  const AboutModal = customizationService.getCustomization(
    'ohif.aboutModal'
  ) as coreTypes.MenuComponentCustomization;
  const UserPreferencesModal = customizationService.getCustomization(
    'ohif.userPreferencesModal'
  ) as coreTypes.MenuComponentCustomization;

  const menuOptions = [
    {
      title: AboutModal?.menuTitle ?? t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: AboutModal?.title ?? t('AboutModal:About MED VIEWER'),
          containerClassName: AboutModal?.containerClassName ?? 'max-w-md',
        }),
    },
    {
      title: UserPreferencesModal?.menuTitle ?? t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          content: UserPreferencesModal,
          title: UserPreferencesModal?.title ?? t('UserPreferencesModal:User preferences'),
          containerClassName:
            UserPreferencesModal?.containerClassName ?? 'flex max-w-4xl p-6 flex-col',
        }),
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(`/logout?redirect_uri=${encodeURIComponent(window.location.href)}`);
      },
    });
  }

  return { menuOptions, appConfig };
}

AdminPanel.propTypes = {
  isLoadingData: PropTypes.bool,
  servicesManager: PropTypes.object.isRequired,
};

export default AdminPanel;
